import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Zap, Users, TrendingUp, ArrowRight } from 'lucide-react';
import CTASection from '../components/CTASection';
import ChaosToOrder from '../components/ChaosToOrder';
import SEO from '../components/SEO';
import GradientOverlay from '../components/GradientOverlay';
import LogomarkPattern from '../components/LogomarkPattern';

const Home = () => {
  const services = [
  {
    icon: Target,
    title: 'CRM Implementation & Optimisation',
    description: 'Transform your sales process with a CRM system that your team will actually use.',
    image: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b',
    link: '/services/crm-implementation'
  },
  {
    icon: Zap,
    title: 'CRM and ERP Integration',
    description: 'Connect your front and back office systems for a seamless flow of information.',
    image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86',
    link: '/services/erp-integration'
  },
  {
    icon: TrendingUp,
    title: 'Smart CRM (AI Market Intelligence)',
    description: 'Leverage AI to predict customer behaviour and uncover hidden opportunities.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
    link: '/services/smart-crm'
  },
  {
    icon: Users,
    title: 'Smart Leads Generation',
    description: 'Stop chasing bad leads. Get qualified B2B prospects delivered to your pipeline.',
    image: 'https://images.unsplash.com/photo-1515169067868-5387ec356754',
    link: '/services/lead-generation'
  },
  {
    icon: Zap,
    title: 'AI Enablement Programmes',
    description: 'Build real AI capability within your organisation, not just talk about it.',
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0',
    link: '/services/ai-enablement'
  }];


  const principles = [
  {
    title: 'Clarity & Candour',
    description: 'We believe in straight talk and transparent processes. We demystify technology and provide clear, actionable roadmaps. No jargon, no hype—just honest guidance.'
  },
  {
    title: 'Pragmatic Innovation',
    description: 'We are not about chasing trends. We are about applying the right technology, at the right time, to solve the right problem. Our solutions are innovative yet practical, designed for real-world business impact.'
  },
  {
    title: 'Decisive Action',
    description: 'We believe in moving forward with purpose. We help our clients make informed decisions and then execute with precision and efficiency, delivering results, not just reports.'
  },
  {
    title: 'Enduring Partnership',
    description: 'Our goal is to build lasting relationships based on trust and mutual success. We empower our clients with the knowledge and systems they need to not just solve today\'s problems, but to own their future growth.'
  },
  {
    title: 'Excellence in Resolution',
    description: 'We are relentless in our pursuit of effective, elegant, and robust solutions. Our work isn\'t done until the problem is truly resolved.'
  }];


  const testimonials = [
  {
    quote: "Resolv Digital didn't just implement a CRM—they transformed how our sales team operates. For the first time, we have complete visibility into our pipeline.",
    author: 'Sarah Mitchell',
    company: 'Director, Precision Engineering Group'
  },
  {
    quote: "After years of technology investments that went nowhere, Resolv Digital delivered exactly what we needed. They resolved our data chaos in months, not years.",
    author: 'James Chen',
    company: 'CEO, Meridian Logistics'
  }];


  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="CRM, ERP & AI Systems Consultants Gold Coast"
        description="Gold Coast's leading digital transformation consultancy. Expert CRM implementation, ERP integration, and AI enablement services for Australian SMEs and scale-ups. Transform chaos into clarity."
        keywords="CRM Gold Coast, ERP Gold Coast, AI Systems Gold Coast, Digital Transformation Australia, CRM Consultant Brisbane, ERP Integration Queensland, AI Enablement, Business Automation Gold Coast"
        canonical="https://resolvdigital.com.au/"
      />
      {/* Hero Section - LIGHT with Logomark Pattern */}
      <section style={{
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        position: 'relative',
        padding: '120px 40px 80px',
        overflow: 'hidden'
      }}>
        {/* Gradient Overlay */}
        <GradientOverlay variant="subtle" opacity={0.2} />
        
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.03} density="sparse" tint="teal" />
        
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(13, 115, 119, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(201, 162, 39, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />

        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            maxWidth: '900px'
          }}>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 7vw, 6rem)',
              lineHeight: 1,
              background: 'linear-gradient(135deg, #0a4447 0%, #0D7377 50%, #C9A227 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textTransform: 'uppercase',
              marginBottom: '32px',
              letterSpacing: '-0.02em'
            }}>
              Stop Buying Hype.<br />Start Getting Real Solutions.
            </h1>
            
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
              lineHeight: 1.5,
              color: '#0a4447',
              marginBottom: '48px',
              maxWidth: '700px'
            }}>
              We deliver clear, decisive digital transformation solutions that solve your business problems. For good.
            </p>

            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              <Link to="/contact" className="btn-primary" style={{
                fontSize: '1.125rem',
                padding: '1.2em 2.5em',
                background: 'linear-gradient(135deg, #0D7377 0%, #0a4447 100%)',
                color: '#ffffff',
                border: 'none'
              }}>
                Explore Our Resolutions
              </Link>
              <Link to="/contact" style={{
                fontSize: '1.125rem',
                padding: '1.2em 2.5em',
                backgroundColor: 'transparent',
                border: '2px solid #0D7377',
                color: '#0D7377',
                borderRadius: '10rem',
                textDecoration: 'none',
                fontFamily: 'Lato, sans-serif',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0D7377';
                e.target.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#0D7377';
              }}>
                Book a Free Assessment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Chaos to Order Section - LIGHT with Chaos Animation Background */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Chaos to Order Animation as Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        {/* Subtle gradient overlay to blend animation */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            {/* Left Content */}
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                In a World of Chaos,<br />We Bring Order and Direction.
              </h2>
              
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#0a4447',
                marginBottom: '20px'
              }}>
                Tech and AI are becoming chaotic. Every day, there are 100 new tools and new ways 
                of getting things done, to the point we feel lost and overwhelmed.
              </p>

              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#0a4447',
                marginBottom: '20px'
              }}>
                Our focus is to help you understand what can really help your business and solve 
                real challenges that actually move the needle.
              </p>

              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#0a4447',
                fontWeight: 600
              }}>
                We help you implement tech that brings <span style={{ color: '#C9A227', fontWeight: 700 }}>real and measurable ROI</span>. 
                In a world of chaos, we bring order and direction to save your money, time and scale your business.
              </p>
            </div>

            {/* Right - Logomark */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              minHeight: '400px'
            }}>
              <img
                src="https://customer-assets.emergentagent.com/job_digital-transform-45/artifacts/zhvprbqi_logomark_web_large_1024x1024.png"
                alt="Resolv Digital"
                style={{
                  width: '80%',
                  maxWidth: '400px',
                  height: 'auto',
                  filter: 'drop-shadow(0 0 40px rgba(13, 115, 119, 0.3))',
                  animation: 'float 6s ease-in-out infinite'
                }} />

            </div>
          </div>
        </div>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </section>

      {/* Services Overview - LIGHT with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '80px'
          }}>
            {/* Small logomark above heading */}
            <img
              src="https://customer-assets.emergentagent.com/job_digital-transform-45/artifacts/zhvprbqi_logomark_web_large_1024x1024.png"
              alt=""
              style={{
                width: '60px',
                height: 'auto',
                margin: '0 auto 32px',
                display: 'block',
                opacity: 0.8
              }} />

            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              lineHeight: 1.1,
              background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textTransform: 'uppercase',
              marginBottom: '24px'
            }}>Our (Re)solutions

            </h2>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.25rem',
              lineHeight: 1.6,
              color: '#0a4447',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Practical solutions designed to permanently resolve your most pressing business challenges.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
            gap: '40px'
          }}>
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Link
                  key={index}
                  to={service.link}
                  style={{
                    textDecoration: 'none',
                    display: 'block'
                  }}>

                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(13, 115, 119, 0.2)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.borderColor = '#0D7377';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(13, 115, 119, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(13, 115, 119, 0.2)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                  }}>

                    <div style={{
                      height: '200px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <img
                        src={service.image}
                        alt={service.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }} />

                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, transparent, rgba(10, 68, 71, 0.7))'
                      }} />
                    </div>
                    <div style={{
                      padding: '32px',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <IconComponent size={40} style={{
                        color: '#0D7377',
                        marginBottom: '20px'
                      }} />
                      <h3 style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        lineHeight: 1.3,
                        color: '#0a4447',
                        marginBottom: '16px'
                      }}>
                        {service.title}
                      </h3>
                      <p style={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        color: '#4a5568',
                        marginBottom: '24px',
                        flex: 1
                      }}>
                        {service.description}
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#C9A227',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Learn More <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                      </div>
                    </div>
                  </div>
                </Link>);

            })}
          </div>
        </div>
      </section>

      {/* Philosophy Section - LIGHT with Chaos Animation Background */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Chaos to Order Animation as Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        {/* Subtle gradient overlay to blend animation */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '80px'
          }}>
            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              lineHeight: 1.1,
              background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textTransform: 'uppercase',
              marginBottom: '24px'
            }}>
              Our Philosophy
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '60px'
          }}>
            {principles.map((principle, index) =>
            <div key={index} style={{
              textAlign: 'center',
              position: 'relative'
            }}>
                {/* Larger logomark decoration (30% bigger: 40px -> 52px) */}
                <img
                src="https://customer-assets.emergentagent.com/job_digital-transform-45/artifacts/zhvprbqi_logomark_web_large_1024x1024.png"
                alt=""
                style={{
                  width: '52px',
                  height: 'auto',
                  margin: '0 auto 24px',
                  display: 'block',
                  opacity: 0.8,
                  filter: 'drop-shadow(0 2px 8px rgba(13, 115, 119, 0.3))'
                }} />

                <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                lineHeight: 1.3,
                color: '#0a4447',
                marginBottom: '20px'
              }}>
                  {principle.title}
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                color: '#4a5568'
              }}>
                  {principle.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials - Hidden for now */}
      {/* 
          <section style={{
           padding: '120px 40px',
           backgroundColor: '#084144',
           backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2)',
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           position: 'relative'
          }}>
           <div style={{
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             backgroundColor: 'rgba(8, 65, 68, 0.92)'
           }} />
           <div className="container" style={{
             maxWidth: '1200px',
             margin: '0 auto',
             position: 'relative',
             zIndex: 1
           }}>
             <div style={{
               textAlign: 'center',
               marginBottom: '80px'
             }}>
               <h2 style={{
                 fontFamily: 'Montserrat, sans-serif',
                 fontWeight: 900,
                 fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                 lineHeight: 1.1,
                 color: '#C9A227',
                 textTransform: 'uppercase',
                 marginBottom: '24px'
               }}>
                 Client Success Stories
               </h2>
             </div>
              <div style={{
               display: 'grid',
               gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
               gap: '40px'
             }}>
               {testimonials.map((testimonial, index) => (
                 <div key={index} style={{
                   backgroundColor: 'rgba(10, 93, 96, 0.7)',
                   backdropFilter: 'blur(10px)',
                   border: '1px solid rgba(201, 162, 39, 0.3)',
                   borderRadius: '8px',
                   padding: '48px',
                   transition: 'all 0.3s ease'
                 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.borderColor = '#C9A227';
                   e.currentTarget.style.backgroundColor = 'rgba(10, 93, 96, 0.9)';
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                   e.currentTarget.style.backgroundColor = 'rgba(10, 93, 96, 0.7)';
                 }}
                 >
                   <p style={{
                     fontFamily: 'Lato, sans-serif',
                     fontSize: '1.125rem',
                     lineHeight: 1.7,
                     color: '#ffffff',
                     marginBottom: '32px',
                     fontStyle: 'italic'
                   }}>
                     "{testimonial.quote}"
                   </p>
                   <div>
                     <p style={{
                       fontFamily: 'Montserrat, sans-serif',
                       fontWeight: 700,
                       fontSize: '1rem',
                       color: '#C9A227',
                       marginBottom: '4px'
                     }}>
                       {testimonial.author}
                     </p>
                     <p style={{
                       fontFamily: 'Lato, sans-serif',
                       fontSize: '0.95rem',
                       color: '#b8d8da'
                     }}>
                       {testimonial.company}
                     </p>
                   </div>
                 </div>
               ))}
             </div>
           </div>
          </section>
          */
      }

      {/* CTA Section */}
      <CTASection />
    </div>);

};

export default Home;